import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

/** Shared layout/field primitives for admin create/edit forms. */
export function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className='tw:rounded-lg tw:border tw:bg-card tw:p-6'>
      <h2 className='tw:mb-5 tw:text-sm tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground'>
        {title}
      </h2>
      <div className='tw:grid tw:gap-5 tw:sm:grid-cols-2'>{children}</div>
    </section>
  );
}

export function Field({
  label,
  required,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <Label className='tw:mb-1.5 tw:block'>
        {label}
        {required && <span className='tw:text-destructive'> *</span>}
      </Label>
      {children}
    </div>
  );
}

export function ToggleField({
  label,
  checked,
  onCheckedChange,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className='tw:flex tw:items-center tw:justify-between tw:rounded-md tw:border tw:px-4 tw:py-2.5'>
      <Label className='tw:cursor-pointer'>{label}</Label>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

/**
 * A calendar-date (`yyyy-mm-dd`) input that reports every change.
 *
 * No draft state or blur-commit, unlike `AutosaveTextInput`: a native date input
 * fires `change` only once a whole date has been picked (or the field cleared),
 * so there is never a half-typed value to hold back — which also means an
 * autosaving form can wire `onChange` straight to its commit.
 */
export function DateInput({
  value,
  onChange,
  min,
  max,
}: {
  /** `yyyy-mm-dd`, or '' for nothing chosen. */
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
}) {
  return <Input type='date' value={value} min={min} max={max} onChange={(e) => onChange(e.target.value)} />;
}

/** A Select bound to a label map (value -> label). */
export function SelectInput<T extends string>({
  value,
  options,
  onValueChange,
}: {
  value: T;
  options: Record<T, string>;
  onValueChange: (value: string) => void;
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className='tw:w-full'>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {(Object.entries(options) as [T, string][]).map(([key, label]) => (
          <SelectItem key={key} value={key}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
